import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck, UserRound } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { publicApi } from '../utils/api';

const GOOGLE_SIGNIN_ALLOWED_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
  'wowrolla-ai.app',
  'wowrolla.ca',
  'www.wowrolla.ca',
]);

const loadGoogleScript = () => new Promise((resolve, reject) => {
  if (window.google?.accounts?.id) {
    resolve();
    return;
  }

  const existing = document.querySelector('script[data-google-identity="true"]');
  if (existing) {
    existing.addEventListener('load', resolve, { once: true });
    existing.addEventListener('error', reject, { once: true });
    return;
  }

  const script = document.createElement('script');
  script.src = 'https://accounts.google.com/gsi/client';
  script.async = true;
  script.defer = true;
  script.dataset.googleIdentity = 'true';
  script.onload = resolve;
  script.onerror = reject;
  document.head.appendChild(script);
});

const ClientLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, loginPublic, registerPublic, loginPublicWithGoogle } = useAuth();
  const [mode, setMode] = useState('login');
  const [googleClientId, setGoogleClientId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [form, setForm] = useState({ identifier: '', email: '', username: '', password: '', fullName: '' });
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);

  const statusNotice = useMemo(() => {
    const search = new URLSearchParams(location.search);
    const statusValue = search.get('status') || '';
    if (statusValue === 'confirmed_account') return 'Your email is confirmed. You can now log in to continue building your business details.';
    if (statusValue === 'invalid_account_confirmation') return 'That confirmation link is invalid or expired. Create the account again to receive a fresh email.';
    return '';
  }, [location.search]);

  const isGoogleOriginAllowed = useMemo(
    () => GOOGLE_SIGNIN_ALLOWED_HOSTS.has(window.location.hostname),
    []
  );

  const redirectPath = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return search.get('redirect') || '/dashboard/stack';
  }, [location.search]);

  useEffect(() => {
    if (isAuthenticated && (user?.role || '').toLowerCase() === 'user') {
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectPath, user]);

  useEffect(() => {
    let mounted = true;
    const initGoogle = async () => {
      try {
        const configResponse = await publicApi.getPublicAuthConfig();
        const clientId = configResponse.data?.google_client_id || '';
        if (!mounted || !clientId) {
          return;
        }
        setGoogleClientId(clientId);
        if (!isGoogleOriginAllowed) {
          return;
        }
        await loadGoogleScript();
        if (!mounted || !window.google?.accounts?.id) {
          return;
        }
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response) => {
            setBusy(true);
            setError('');
            const result = await loginPublicWithGoogle(response.credential);
            setBusy(false);
            if (!result.success) {
              setError(result.error || 'Google login failed');
              return;
            }
            navigate(redirectPath, { replace: true });
          },
        });
        window.google.accounts.id.renderButton(document.getElementById('google-signin-button'), {
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          width: 320,
          text: mode === 'register' ? 'signup_with' : 'signin_with',
        });
      } catch {
        if (mounted) {
          setGoogleClientId('');
        }
      }
    };

    initGoogle();
    return () => {
      mounted = false;
    };
  }, [isGoogleOriginAllowed, loginPublicWithGoogle, mode, navigate, redirectPath]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');

    const result = mode === 'login'
      ? await loginPublic(form.identifier || form.email, form.password)
      : await registerPublic({
          email: form.email,
          username: form.username,
          password: form.password,
          fullName: form.fullName || 'Wowrolla Client',
          newsletterOptIn,
        });

    setBusy(false);

    if (!result.success) {
      setError(result.error || 'Authentication failed');
      return;
    }

    if (mode === 'register' && result.requiresConfirmation) {
      setMode('login');
      setForm((prev) => ({ ...prev, identifier: prev.email, password: '', email: prev.email }));
      setNotice(result.message || 'Check your email to confirm your account before logging in.');
      return;
    }

    navigate(redirectPath, { replace: true });
  };

  return (
    <section className="container mx-auto px-6 py-16" data-testid="client-login-page">
      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 items-start">
        <div className="terminal-grid p-8">
          <p className="text-xs uppercase font-mono mb-3" style={{ color: 'var(--text-muted)' }}>Client access</p>
          <h1 className="text-4xl font-black mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>
            Sync your stack, keep your session, and pick up where you left off.
          </h1>
          <p className="text-base mb-6 max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
            Use a simple username/password account or Google sign-in. Your dashboard, saved tools, and alert preferences persist between visits.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
              <ShieldCheck size={20} className="mb-3" style={{ color: 'var(--accent-primary)' }} />
              <p className="font-semibold text-white mb-2">Persistent sessions</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>The app restores your authenticated session from local storage on reload.</p>
            </div>
            <div className="p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
              <UserRound size={20} className="mb-3" style={{ color: 'var(--accent-primary)' }} />
              <p className="font-semibold text-white mb-2">Client-ready access</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Designed for external users, not just admins, with a path into My Stack and consulting flows.</p>
            </div>
          </div>
        </div>

        <div className="terminal-grid p-8">
          <div className="flex gap-2 mb-6">
            <button onClick={() => setMode('login')} className="px-4 py-2 border text-sm font-semibold" style={{ borderColor: mode === 'login' ? 'var(--accent-primary)' : 'var(--border-subtle)', color: mode === 'login' ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>
              Login
            </button>
            <button onClick={() => setMode('register')} className="px-4 py-2 border text-sm font-semibold" style={{ borderColor: mode === 'register' ? 'var(--accent-primary)' : 'var(--border-subtle)', color: mode === 'register' ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>
              Create account
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'login' ? (
              <label className="block text-sm" style={{ color: 'var(--text-secondary)' }}>
                Email or username
                <input value={form.identifier} onChange={(event) => setForm((prev) => ({ ...prev, identifier: event.target.value }))} className="w-full mt-2 px-3 py-3 bg-transparent border border-white/20 text-white" placeholder="you@company.com or username" required />
              </label>
            ) : (
              <>
                <label className="block text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Full name
                  <input value={form.fullName} onChange={(event) => setForm((prev) => ({ ...prev, fullName: event.target.value }))} className="w-full mt-2 px-3 py-3 bg-transparent border border-white/20 text-white" placeholder="Jordan Smith" required />
                </label>
                <label className="block text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Username
                  <input value={form.username} onChange={(event) => setForm((prev) => ({ ...prev, username: event.target.value }))} className="w-full mt-2 px-3 py-3 bg-transparent border border-white/20 text-white" placeholder="jordansmith" required />
                </label>
                <label className="block text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Email
                  <input type="email" value={form.email} onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))} className="w-full mt-2 px-3 py-3 bg-transparent border border-white/20 text-white" placeholder="you@company.com" required />
                </label>
                <label className="flex items-start gap-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <input
                    type="checkbox"
                    checked={newsletterOptIn}
                    onChange={(event) => setNewsletterOptIn(event.target.checked)}
                    className="mt-1"
                  />
                  <span>
                    Subscribe to the Wowrolla newsletter for launch updates and tool intel. Client subscribers get a free AI assessment-call offer.
                  </span>
                </label>
              </>
            )}
            <label className="block text-sm" style={{ color: 'var(--text-secondary)' }}>
              Password
              <input type="password" value={form.password} onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))} className="w-full mt-2 px-3 py-3 bg-transparent border border-white/20 text-white" placeholder="--------" required />
            </label>

            {(statusNotice || notice) && <p className="text-sm" style={{ color: '#9ef0b8' }}>{notice || statusNotice}</p>}
            {error && <p className="text-sm" style={{ color: '#ff7a7a' }}>{error}</p>}

            <button type="submit" disabled={busy} className="btn-primary w-full">
              {busy ? 'Working...' : mode === 'login' ? 'Login to my account' : 'Create my account'}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px" style={{ background: 'var(--border-subtle)' }} />
            <span className="text-xs uppercase font-mono" style={{ color: 'var(--text-muted)' }}>or</span>
            <div className="flex-1 h-px" style={{ background: 'var(--border-subtle)' }} />
          </div>

          {isGoogleOriginAllowed && <div id="google-signin-button" className="min-h-[44px]" />}
          {!googleClientId && (
            <p className="text-sm mt-3" style={{ color: 'var(--text-secondary)' }}>
              Google sign-in becomes available after a Google client ID is saved in Admin > SEO/ops tabs.
            </p>
          )}
          {googleClientId && !isGoogleOriginAllowed && (
            <p className="text-sm mt-3" style={{ color: 'var(--text-secondary)' }}>
              Google sign-in is only enabled on approved domains. Use email/password on preview deployments.
            </p>
          )}

          <p className="text-sm mt-6" style={{ color: 'var(--text-secondary)' }}>
            Need the admin console instead? <Link to="/admin" style={{ color: 'var(--accent-primary)' }}>Use the admin login</Link>.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ClientLoginPage;

